/**
 * AuthClient — core OIDC client wrapping `oidc-client-ts`.
 *
 * Talks to auth.molaris.ai via the standard OIDC endpoints:
 *   - GET  /authorize        (PKCE auth-code start)
 *   - POST /token            (code → tokens; refresh)
 *   - GET  /me               (cold-load session restore)
 *   - GET  /me/tenants       (tenant memberships for the picker)
 *   - POST /refresh          (silent refresh; supports target_tenant_id)
 *   - POST /logout           (server-side denylist + cookie clear)
 *
 * The auth service does its own Cognito interaction server-side; we never
 * import `amazon-cognito-identity-js` from the browser.
 *
 * Refresh token lives in the host-only `__Host-molaris-refresh` cookie on the
 * auth host — httpOnly / Secure / SameSite=None, no Domain (NOT `.molaris.ai`;
 * see services/auth-service/src/cookies/refresh.ts). Browser JS cannot read it —
 * every refresh/me/logout call uses `credentials: "include"` so the cookie flows
 * automatically. (The legacy `molaris_refresh` cookie is NOT purely deletion-only:
 * parseRefreshCookie still accepts it as a read fallback for the duration of the
 * ENG-2127 migration. The read is by NAME — a request header carries no Domain —
 * and a host-only legacy variant exists alongside the parent-domain ones; see
 * LEGACY_PARENT_COOKIE_DOMAINS and clearLegacyRefreshCookies in
 * services/auth-service/src/cookies/refresh.ts.)
 */
import {
  UserManager,
  WebStorageStateStore,
  type User as OidcUser,
  type UserManagerSettings,
} from "oidc-client-ts";
import { decodeJwt } from "jose";
import type { AuthClientTokenReader } from './client-token-reader.js';
import {
  loadAuthConfig,
  safeWarn,
  sanitizePostLogoutUri,
  type AuthConfig,
} from "./config.js";
import { createDevAuthClient } from "./dev-client.js";
// LOCAL-STACK dev auth (our zero-AWS local-stack). Separate from dev-client.ts
// (the packages/local-backend VITE_DEV_USER path): local-stack drives a
// pick-user cookie flow at http://localhost:*/dev-auth, so the auth-client is
// selected by VITE_AUTH_BASE_URL pointing at localhost, not by VITE_DEV_USER.
import { createDevAuthClient as createLocalStackAuthClient, isDevAuthBaseUrl } from "./dev-mode.js";
import {
  createSilentRefreshScheduler,
  TransientRefreshError,
  type SilentRefreshScheduler,
} from "./silent-refresh.js";
import { createSignOutFlow, type SignOutOptions } from "./sign-out-flow.js";
import type { SignOutResult } from "./sign-out.js";
// ENG-2756 / PR #1813 follow-up 2 — four closure-free helpers live in
// client-internals.ts so this file stays under the 500-effective-line cap.
import {
  createDeferred,
  isOidcCallbackInProgress,
  notifyListeners,
} from "./client-internals.js";
import type {
  AuthError,
  AuthErrorListener,
  Session,
  SessionListener,
  Tenant,
} from "./types.js";

// ENG-2907 — SignOutOptions now lives with the sign-out flow (sign-out-flow.ts);
// re-exported here so the client's public surface (index.ts) is unchanged.
export type { SignOutOptions } from "./sign-out-flow.js";

/** Subset of the access-token claim shape we read on the client. */
interface AccessTokenClaims {
  sub: string;
  iat: number;
  exp: number;
  /** Single-element array per services/auth-service/src/tokens/mint.ts
   *  `signWithHeader` (`.setAudience([audience])`); `jose`'s `decodeJwt`
   *  types it loosely as string | string[], so both shapes are handled. */
  aud?: string | string[];
  session_id?: string;
  molaris?: {
    user_global_id?: string;
    user_tenant_id?: string;
    user_local_id?: string;
    portal_role?: Session["portalRole"];
    internal_role_ids?: string[];
    org_unit_scope_ids?: string[];
    product_entitlements?: string[];
    mfa_verified?: boolean;
    mfa_verified_at?: string | null;
    is_jit_grant?: boolean;
    jit_grant_id?: string | null;
    email?: string;
    full_name?: string | null;
  };
  email?: string;
  name?: string;
}


export interface SignInOptions {
  /** Pre-select a tenant; sent as `target_tenant_id` to /authorize. */
  tenantId?: string;
  /** Override returnTo (where to land after callback). Default: current URL. */
  returnTo?: string;
  /**
   * ENG-101 — OIDC `prompt` param. Pass `'login'` on an explicit user
   * "Sign in" action (the hosted form's identity check must not be
   * short-circuited by a stale refresh cookie (host-only on the shared auth
   * host) belonging to a DIFFERENT account — see
   * services/auth-service/src/oidc/authorize.ts).
   * Leave unset for background/resume calls that should keep silently
   * reusing an existing session when one is present.
   */
  prompt?: "login" | "none";
  /**
   * ENG-101 — the email the user is attempting to sign in as, when the
   * caller already has it (e.g. typed into an on-page field before the
   * redirect). Sent as OIDC `login_hint`; the server falls back to the
   * sign-in form instead of silently resolving to a cookie session for a
   * DIFFERENT identity. Optional — omit when the caller doesn't know the
   * intended email yet (the hosted form still collects it either way).
   */
  loginHint?: string;
}

export interface AuthClient extends AuthClientTokenReader {
  /** Start the PKCE redirect flow against /authorize. */
  signIn(options?: SignInOptions): Promise<void>;
  /** Parse the callback URL → exchange code for tokens → return Session. */
  handleCallback(): Promise<Session>;
  /**
   * Server-side logout + local wipe + redirect to postLogoutUri.
   *
   * ENG-2907 — returns a structured `SignOutResult` instead of `void`: the
   * old signature inspected neither `res.status` nor `res.ok`, so a failed
   * /logout was indistinguishable from success. `verdict: 'signed_out'` means
   * ONLY "/logout returned a bare 200" — NOT "confirmed revoked": the server's
   * verify-failure path also returns 200 without revoking (see sign-out.ts).
   * The GET /userinfo confirmation probe that would narrow that gap, and
   * verdict-gated navigation, are NOT shipped (ENG-3153 — the ENG-2907 T8
   * acceptance never landed); T3 always emits `probe: 'not_run'`. What ships is
   * the ENG-2756 per-instance latch below, and it protects only THIS surviving
   * document — cross-navigation / reload resurrection after a failed /logout
   * remains open (durable closers: ENG-3157 verdict-gated nav, ENG-2713 server
   * revocation-on-retry). The local wipe always completes and this NEVER throws,
   * regardless of the verdict.
   *
   * ENG-2756 — calling this also LATCHES the client torn down, on every verdict
   * including `not_signed_out`. After it, `getCurrentUser()` resolves null without
   * probing, `getAccessToken()` returns null, and `refreshSession()` /
   * `switchTenant()` reject without reaching the wire, until a successful
   * `handleCallback()` clears the latch. The latch is in-memory and per client
   * instance: it does not survive a page reload and says nothing about whether the
   * server-side session or the refresh cookie is actually revoked — that is what
   * the verdict is for.
   */
  signOut(options?: SignOutOptions): Promise<SignOutResult>;
  /** Silent refresh with target_tenant_id; updates in-memory session. */
  switchTenant(targetTenantId: string): Promise<Session>;
  /**
   * Force a silent refresh of the CURRENT session (no tenant switch) and
   * return the refreshed Session.
   *
   * `getAccessToken()` only refreshes when the cached token is near/past
   * expiry, and `getCurrentUser()` returns the cached session outright — so
   * neither can pick up a server-side session mutation that leaves `exp`
   * untouched. Step-up MFA is exactly that case: the `/mfa` respond endpoints bumps
   * `sessions.mfa_verified_at` server-side, and only a token re-mint
   * (`POST /refresh`, which reads `session.mfaVerifiedAt`) surfaces the new
   * `molaris.mfa_verified_at` claim the resolver-side gate reads. Without
   * this the retry after a successful step-up replays the SAME stale token
   * and is rejected again.
   *
   * ENG-2756 contract on failure: rejects (never resolves) — but a TRANSIENT
   * failure (a 5xx from /refresh, or a network blip, while the current token is
   * still valid) rejects with `TransientRefreshError` and does NOT tear the
   * session down; the caller may retry or ignore it, and the background scheduler
   * keeps the session alive. A DEFINITIVE failure (4xx, or the token already
   * expired) rejects with a plain Error AFTER tearing the session down (an
   * `onAuthError` `session_expired` fires). A THIRD case (ENG-2756 fix round 2)
   * also rejects with a plain Error but fires NO `onAuthError`: a refresh that
   * completes AFTER the client was already torn down (signOut / emitAuthError) or
   * re-signed-in has its token DISCARDED so it cannot resurrect a dead session —
   * the preceding teardown already emitted whatever error was appropriate. Callers
   * that today only care about the happy path are unaffected; those that want to
   * distinguish an outage from a sign-out can `instanceof TransientRefreshError`.
   */
  refreshSession(): Promise<Session>;
  /** Tenants the current user can switch into. */
  listTenantMemberships(): Promise<Tenant[]>;
  /** Subscribe to session changes (sign-in / sign-out / tenant switch). */
  onSessionChange(listener: SessionListener): () => void;
  /** Subscribe to auth-error events (called by Apollo error link on 401). */
  onAuthError(listener: AuthErrorListener): () => void;
  /** Manually trigger an auth-error (used by Apollo error link). */
  emitAuthError(error?: AuthError): void;
  /** The resolved config (read-only, useful for debug + tests). */
  readonly config: AuthConfig;
}

export interface CreateAuthClientOptions extends Partial<AuthConfig> {
  /** Inject a fetch impl (tests). Defaults to global fetch. */
  fetchImpl?: typeof fetch;
}

/** Construct an AuthClient. Call this once per app at module init. */
export function createAuthClient(options: CreateAuthClientOptions = {}): AuthClient {
  // Local-backend mode (packages/local-backend): no auth.molaris.ai locally,
  // so the OIDC shell is replaced by a static dev session. Gated on Vite's
  // DEV flag AND VITE_DEV_USER: `vite build` (any mode) sets DEV=false, so
  // this cannot ship even if a stray .env.local leaks VITE_DEV_USER into a
  // production/staging build. See dev-client.ts.
  const env =
    typeof import.meta !== "undefined"
      ? (import.meta as { env?: Record<string, string | boolean | undefined> }).env
      : undefined;
  const devUser = env?.["DEV"] ? (env["VITE_DEV_USER"] as string | undefined) : undefined;
  if (devUser) {
    return createDevAuthClient(devUser, options);
  }

  const { fetchImpl, ...configOverrides } = options;
  const config = loadAuthConfig(configOverrides);

  // LOCAL-STACK dev-auth bypass — when VITE_AUTH_BASE_URL points at the
  // local-stack (http://localhost*/dev-auth) skip OIDC entirely and use the
  // pick-user cookie flow. Throwaway; never active in deployed builds (real
  // builds set VITE_AUTH_BASE_URL=https://auth.molaris.ai).
  if (isDevAuthBaseUrl(config.authBaseUrl)) {
    return createLocalStackAuthClient(config);
  }

  // ENG-2907 — sanitise postLogoutUri at CONSTRUCTION, NON-FATALLY (never throws:
  // this runs at module top level, so a throw is white-screen-at-boot). Extracted
  // to config.ts (sanitizePostLogoutUri) beside safeOrigin/postLogoutUri. NOTE:
  // `config` may be loadAuthConfig's MEMOISED instance — getDefaultClient()
  // (index.ts) calls createAuthClient() with NO overrides, so `loadAuthConfig({})`
  // returns its cached object — so this reassignment can mutate the shared config
  // other holders (e.g. apps/vendor's default client) also reference. That is safe
  // ONLY because sanitizePostLogoutUri is idempotent: re-running it on an
  // already-resolved URL yields the same value, so a second holder cannot observe
  // a half-rewritten one.
  config.postLogoutUri = sanitizePostLogoutUri(config.postLogoutUri);

  const doFetch: typeof fetch = fetchImpl ?? globalThis.fetch.bind(globalThis);

  // -- oidc-client-ts wiring -------------------------------------------------

  const userManagerSettings: UserManagerSettings = {
    authority: config.authBaseUrl,
    client_id: config.cognitoAppClientId,
    redirect_uri: config.redirectUri,
    post_logout_redirect_uri: config.postLogoutUri,
    response_type: "code",
    scope: `openid email profile aud:${config.audience}`,
    // Refresh-token cookie lives on the auth domain; we don't store it
    // client-side. oidc-client-ts only needs scratch storage for PKCE state.
    userStore: new WebStorageStateStore({
      store: typeof window !== "undefined" ? window.sessionStorage : undefined,
    }),
    automaticSilentRenew: false, // we run our own scheduler (see silent-refresh.ts)
    monitorSession: false,
    loadUserInfo: false,
    // 2026-04-27 — without this, the cross-origin POST to /token (auth.* from
    // app.*) is sent without credentials, so the browser DROPS the Set-Cookie
    // refresh cookie that auth-service emits. Page reload then has no cookie,
    // /me returns 401, and the user is logged out. With credentials:'include'
    // the browser saves the cookie, and authFetch (already credentials:include)
    // sends it on subsequent /me + /refresh calls.
    fetchRequestCredentials: "include",
  };

  const userManager = new UserManager(userManagerSettings);

  // -- in-memory state -------------------------------------------------------

  let currentSession: Session | null = null;
  let currentAccessToken: string | null = null;
  let coldLoadInflight: Promise<Session | null> | null = null;
  // ENG-2756 fix round 2 — teardown latch. `tornDown` is the PRIMARY teardown
  // signal and it is STICKY: once a teardown primitive sets it, it stays set until
  // an explicit new sign-in (a successful handleCallback — the ONLY clear site;
  // signIn() no longer clears it, see F-9) clears it. Every adoption site
  // (performRefresh's entry + success paths, adoptOidcUser) and the cold-load
  // entry (getCurrentUser) refuse while it is set, so anything that STARTS after a
  // teardown — a poll, a retry, the auth-link getAccessToken() on this client
  // instance — cannot re-mint a session on a torn-down client. The latch is
  // per-closure, so it is scoped to THIS client instance, not shared across the
  // six apps (F-10, separate ticket). This closes the resurrection whose window
  // opens AFTER the teardown: the counter alone could not, because a refresh that
  // STARTS after the bump captures the post-bump value and adopts freely. The
  // security case is a signOut() whose /logout failed at the network layer while
  // the DOCUMENT OUTLIVES the sign-out. ENG-2907 navigates to postLogoutUri
  // unconditionally, which is not the same as the page being gone: the assign is
  // asynchronous (this document keeps executing until the new one commits), the
  // commit needs the very network that just failed /logout, sign-out-flow.ts's
  // navigateToPostLogout() is a no-op when `window` is undefined, and its
  // assign -> replace -> href chain can fail outright (that module's own tests
  // cover it). Everything the latch refuses runs in that surviving document.
  let tornDown = false;
  // Companion RACE TOKEN, deliberately kept alongside the latch (it is NOT
  // redundant). performRefresh captures it before its network call and re-checks
  // it on the success path. It catches the one case the latch cannot: a teardown
  // followed by a NEW sign-in — which clears the latch — while an OLD refresh is
  // still in flight; that stale token must not clobber the freshly-signed-in
  // session. (Also distinguishes instances in multi-client setups.)
  let authGeneration = 0;

  // ENG-2756 fix round 2 — mark the client torn down. Every teardown primitive
  // (emitAuthError and signOut) calls this so the latch and the race token always
  // move together and the invariant is greppable. NOTE: adoptOidcUser's reject
  // branch is deliberately NOT a teardown primitive (F-11) — a wrong-audience /
  // token-less cached user is not proof the session is dead, so it clears local
  // state without latching.
  function beginTeardown(): void {
    tornDown = true;
    authGeneration += 1;
  }

  // -- ENG-1753: first-load OIDC callback sequencing --------------------------
  //
  // On a fresh first-load sign-in the session is established by handleCallback's
  // /token exchange — the ONLY path that both mints an access token AND sets the
  // host-only refresh cookie (on the auth host). The mount-time cold-load (AuthProvider's
  // getCurrentUser, and getAccessToken via the FlagProvider) fires concurrently;
  // its /me cold-probe carries NEITHER credential on the first pass:
  //   - no bearer — currentAccessToken is still null (adoptOidcUser hasn't run),
  //     so authFetch attaches no Authorization header; and
  //   - no cookie — the refresh cookie is not set until /token returns.
  // So /me 401s and the cold-load resolves to a premature `null` that races the
  // real session handleCallback is minting. #1168 stopped that null from
  // clobbering React state, but the cold-load still RETURNED null — so
  // getAccessToken handed the FlagProvider an empty bearer (/flags 401) and the
  // analytics MFE, whose FlagProvider drives a SECOND cold-load during the
  // callback, still hung on "Finishing sign-in…" and looped / ↔ /auth/callback
  // until a manual reload (where the cookie now exists) recovered it.
  //
  // Fix the SEQUENCING: while this page load is a code callback, the cold-load
  // waits for handleCallback and adopts its result — the session with the access
  // token in hand — instead of issuing an unauthenticated cookie probe that is
  // guaranteed to 401. handleCallback resolves this deferred on success (session)
  // and on failure (null, so a waiter falls through instead of stalling). The
  // `isOidcCallbackInProgress()` guard in getCurrentUser scopes this to the
  // actual callback page: every later cold-load (post-logout, token expiry) uses
  // the normal cookie-probe path unchanged.
  const initialCallbackDeferred = createDeferred<Session | null>();
  // Bounded wait so a callback URL with no handler (should never happen — a
  // redirect_uri always has an AuthCallback page) can't stall the cold-load
  // forever; it falls through to the cookie probe after this window.
  const INITIAL_CALLBACK_WAIT_MS = 15_000;

  const sessionListeners = new Set<SessionListener>();
  const errorListeners = new Set<AuthErrorListener>();

  // -- ENG-138: post-sign-in revocation-probe grace window --------------------
  //
  // The background /me revocation probe in getCurrentUser() below (cached-user
  // fast path) exists to catch a session that was revoked from another tab or
  // product since the OIDC user was cached. Right after a fresh sign-in,
  // though, that same probe was observed 401-ing *spuriously* on staging (see
  // the "Staging regression (2026-07-09)" note below) — almost certainly a
  // brief read-after-write propagation window between /token minting the
  // session and /me's session-store lookup seeing it. A session that was just
  // minted cannot possibly be revoked yet, so there is nothing to catch in
  // that window; skip the probe rather than 401-spam the console on every
  // fresh sign-in. `sessionStorage` (not an in-memory var) because this must
  // survive the full-page reload that follows the OIDC redirect callback.
  const SIGNED_IN_AT_STORAGE_KEY = "clearpath.auth.signedInAt";
  const REVOCATION_PROBE_GRACE_MS = 30_000;

  function markSignedInNow(): void {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.setItem(SIGNED_IN_AT_STORAGE_KEY, String(Date.now()));
    } catch {
      // Storage unavailable (private browsing lockdown, quota) — the grace
      // window is a noise-reduction nicety, not a correctness requirement.
    }
  }

  function withinPostSignInGraceWindow(): boolean {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.sessionStorage.getItem(SIGNED_IN_AT_STORAGE_KEY);
      const signedInAt = raw ? Number(raw) : NaN;
      return Number.isFinite(signedInAt) && Date.now() - signedInAt < REVOCATION_PROBE_GRACE_MS;
    } catch {
      return false;
    }
  }

  // ENG-2907 (F3) — a listener that throws must not abort the emit loop or
  // propagate into the caller. signOut() promises never to reject and always to
  // complete its local wipe; emitSession() runs mid-wipe, so an unguarded throw
  // here would skip removeUser()/sessionStorage cleanup and leave the cached
  // OIDC user (and SIGNED_IN_AT marker) to be re-adopted on the next load.
  // Isolate each listener.
  function emitSession(): void {
    notifyListeners(sessionListeners, currentSession, "onSessionChange");
  }

  function emitAuthError(error?: AuthError): void {
    // ENG-2756 fix round 2 — latch the teardown FIRST (sticky flag + race token,
    // see beginTeardown): any in-flight performRefresh reads the latch on
    // resolution and refuses to adopt, and any later cold-load short-circuits.
    beginTeardown();
    currentSession = null;
    currentAccessToken = null;
    scheduler.cancel();
    // F-3 — also drop the cached oidc-client-ts user. Without this it survives in
    // sessionStorage and the NEXT getCurrentUser() cold-load fast path re-adopts
    // it outright (no race needed). Fire-and-forget: teardown must not block on
    // storage, and the latch + getCurrentUser short-circuit already prevent
    // re-adoption if this is slow or fails.
    void userManager.removeUser().catch(() => {
      // best-effort sessionStorage cleanup
    });
    notifyListeners(errorListeners, error, "onAuthError");
    emitSession();
  }

  // -- Session decoding ------------------------------------------------------

  function sessionFromAccessToken(token: string): Session {
    const claims = decodeJwt(token) as unknown as AccessTokenClaims;
    const m = claims.molaris ?? {};
    return {
      userGlobalId: m.user_global_id ?? claims.sub,
      userLocalId: m.user_local_id ?? "",
      userTenantId: m.user_tenant_id ?? "",
      email: m.email ?? claims.email ?? "",
      fullName: m.full_name ?? claims.name ?? null,
      internalRoleIds: m.internal_role_ids ?? [],
      orgUnitScopeIds: m.org_unit_scope_ids ?? [],
      productEntitlements: m.product_entitlements ?? [],
      mfaVerified: Boolean(m.mfa_verified),
      mfaVerifiedAt: m.mfa_verified_at ?? null,
      portalRole: m.portal_role ?? null,
      isJitGrant: Boolean(m.is_jit_grant),
      jitGrantId: m.jit_grant_id ?? null,
      sessionId: claims.session_id ?? "",
      issuedAt: claims.iat,
      expiresAt: claims.exp,
    };
  }

  /**
   * ENG-119 defense in depth. `config.audience` pinned on every cookie-probe
   * request (see authFetch) is the primary fix — it stops the SERVER from
   * ever handing this app a token minted for a different product. This is a
   * second, independent check on the token this app is about to ADOPT:
   * decode its own `aud` claim (services/auth-service/src/tokens/mint.ts
   * signs a single-element `aud` array per app surface) and refuse to adopt
   * it if it doesn't match this app's configured audience.
   *
   * Deliberately NOT keyed on `molaris.portal_role` / `internal_role_ids` —
   * `portal_role` is a still-unpopulated stub server-side today (see
   * services/auth-service/src/oidc/token.ts, "populated in MOL-518"; ground-
   * truthed 2026-07-16 against a real staging portal-customer token, which
   * came back `portal_role: null`), so it cannot yet distinguish a portal
   * customer from staff. `aud` is signed and verified by the server on
   * every mint (`tokens/mint.ts` `signWithHeader`/`verifyToken`) and is the
   * one claim guaranteed to reflect which product this token is actually
   * scoped to.
   */
  function tokenAudienceMatches(token: string): boolean {
    const { aud } = decodeJwt(token) as unknown as AccessTokenClaims;
    const candidates = Array.isArray(aud) ? aud : typeof aud === "string" ? [aud] : [];
    return candidates.includes(config.audience);
  }

  function adoptOidcUser(user: OidcUser | null): Session | null {
    // ENG-2756 fix round 2 — refuse to adopt while torn down (the sticky latch:
    // a cold-load fast path whose `userManager.getUser()` await straddles a
    // concurrent signOut()/emitAuthError() must not adopt onto a torn-down client,
    // F-3), OR when the cached oidc-client-ts user is absent / token-less /
    // wrong-audience. This branch clears local state and DROPS the cached user,
    // but it does NOT latch (F-11 revert): a wrong-audience / token-less cached
    // user is not proof the SESSION is dead. `userStore` is sessionStorage —
    // per-origin and per-tab — so a wrong-audience cached user is NOT the ENG-119
    // artifact (that attack's artifact is the refresh cookie, host-only on the
    // shared auth host and reachable by every sibling SPA that calls it, defended
    // by the audience-pinned authFetch header + tokenAudienceMatches, neither of
    // which needs the latch). NOT latching in THIS branch keeps a stale-audience
    // cached user (e.g. after a VITE_AUTH_CLIENT_AUDIENCE change) from bricking
    // every cold-load: this branch clears + drops the user, and the same-context
    // cookie-probe path then re-mints a correct-audience session. That cold-load
    // fallthrough is what F-11 above reverted the latch for, and it is what this
    // branch must preserve. handleCallback() routes a token-less / wrong-audience
    // callback through this branch too, but it does NOT rely on this branch failing
    // open: it re-latches at its OWN site whenever adoptOidcUser refuses — on the
    // returned-null path, and on the throw path since fix round 3 made that guard
    // unconditional — so a failed callback no longer falls through to a cookie-minted
    // session (A8): deliberate, fail-closed, and one navigation on the AuthCallback
    // error page recovers. The re-latch is handleCallback's concern, at a different
    // site with different consequences; it is NOT this branch latching. Drop the
    // cached user via removeUser() so the rejected user is not sticky across reloads
    // (D7); the sign-in path re-mints a fresh one.
    // Read the token ONCE. The argument is not "two reads could differ" in the
    // abstract: under the old triple read, a value that was a valid JWT at
    // tokenAudienceMatches and garbage by the time sessionFromAccessToken decoded it
    // threw AFTER `currentAccessToken` had already been assigned — landing in
    // handleCallback's catch with a non-null bearer and no matching session, the
    // mixed state this ticket exists to close. Scoped to WITHIN this function:
    // getCurrentUser's cached-user fast path checks `cached.access_token` before
    // calling us and we read it again here, so the check-then-use still spans that
    // call boundary. Not reachable through oidc-client-ts today either way.
    const accessToken = user?.access_token ?? null;
    if (tornDown || !accessToken || !tokenAudienceMatches(accessToken)) {
      currentSession = null;
      currentAccessToken = null;
      scheduler.cancel();
      void userManager.removeUser().catch(() => {
        // best-effort sessionStorage cleanup
      });
      emitSession();
      return null;
    }
    currentAccessToken = accessToken;
    currentSession = sessionFromAccessToken(accessToken);
    scheduler.schedule(currentSession.expiresAt);
    emitSession();
    return currentSession;
  }

  // -- HTTP helpers ----------------------------------------------------------

  async function authFetch(
    path: string,
    init: RequestInit = {},
  ): Promise<Response> {
    const url = new URL(path, config.authBaseUrl).toString();
    const headers = new Headers(init.headers);
    if (currentAccessToken && !headers.has("authorization")) {
      headers.set("authorization", `Bearer ${currentAccessToken}`);
    }
    if (init.body && !headers.has("content-type")) {
      headers.set("content-type", "application/json");
    }
    // ENG-119 — pin THIS app's audience on every request so the server's
    // cookie-probe routes (/me, /refresh, /logout) verify the refresh cookie
    // (host-only on the shared auth host) against the audience this SPA was
    // built for, not whichever audience the cookie happens to carry.
    //
    // Before this, GET /me and POST /refresh received no `?audience=` /
    // `X-Molaris-Audience` at all (see services/auth-service/src/audience.ts
    // `resolveCookieProbeAudience`), so a caller with NO explicit audience
    // fell back to the cookie's OWN `aud` claim. A portal customer signed
    // into portal.{env}.molaris.ai (cookie aud=clearpath-portal) who then
    // opened app.{env}.molaris.ai (this app, audience=clearpath-main) would
    // cold-load successfully — /me and /refresh happily verified + re-minted
    // a `clearpath-portal`-scoped access token, and this app adopted it as a
    // normal signed-in session (ProtectedRoute only checks "is there a
    // session", not which audience minted it) — rendering the internal
    // lab-staff console shell for a customer identity (ENG-119).
    //
    // With the audience pinned, `resolveCookieProbeAudience` now verifies the
    // cookie against THIS app's audience specifically; a cookie minted for a
    // different app 401s here exactly like a missing cookie would, so
    // ProtectedRoute's existing `!session` branch bounces the visitor to
    // sign-in instead of adopting a wrong-audience session.
    if (!headers.has("x-molaris-audience")) {
      headers.set("x-molaris-audience", config.audience);
    }
    return doFetch(url, {
      ...init,
      headers,
      credentials: "include",
    });
  }

  // -- Silent refresh --------------------------------------------------------

  // ENG-2756 — a refresh failure that is NOT proof the session is dead (a 5xx from
  // /refresh, or a network failure) must not tear the session down: keep the current
  // token while it is still valid and let the scheduler retry within its lead window.
  // Only when the token has ALREADY expired does a failed refresh mean the session is
  // over. With no current session (cold load) there is nothing to protect, so it is
  // transient too — the cold-load caller catches it and returns null, no spurious
  // "session expired" fired. Returns the Error to THROW (never resolves): a
  // TransientRefreshError (the scheduler retries) or, after teardown, a plain Error.
  function transientOrTeardown(cause: unknown): Error {
    if (currentSession && currentSession.expiresAt * 1000 <= Date.now()) {
      const err: AuthError = {
        code: "session_expired",
        message: "Session expired during a refresh outage",
        hint: "Sign in again",
      };
      emitAuthError(err);
      return new Error(err.message);
    }
    return new TransientRefreshError(
      cause instanceof Error ? cause.message : String(cause),
      cause instanceof Error ? { cause } : undefined,
    );
  }

  async function performRefresh(targetTenantId?: string): Promise<Session> {
    // ENG-2756 fix round 2 (F-12) — refuse at ENTRY on a torn-down client, before
    // any network call. A latched client must not even SEND POST /refresh: the
    // server rotates the refresh cookie before this client would discard the token,
    // so a post-signOut refresh could PROLONG the very credential sign-out is
    // ending. refreshSession() and switchTenant() are public and otherwise
    // ungated, and a 4xx on a latched client would run emitAuthError() (re-firing
    // `session_expired` after sign-out) before reaching the success-path guard.
    // Throw a plain Error (never TransientRefreshError) so nothing retries — the
    // teardown was deliberate. A teardown that lands AFTER this entry check has
    // passed, mid-flight, is caught on the success-path check below by the RACE
    // TOKEN alone (beginTeardown bumps authGeneration, so generationAtStart no
    // longer matches). The success-path `tornDown` disjunct is redundant for that
    // case — kept only as defence in depth, decoupling the guard from
    // beginTeardown's set-both invariant rather than adding independent coverage.
    if (tornDown) {
      throw new Error("Refresh on a torn-down client; not sending /refresh");
    }
    // ENG-2756 fix round 2 — capture the race token before the network call. It
    // catches a teardown-then-new-sign-in that lands while this /refresh is in
    // flight (the latch alone cannot: the new sign-in clears it). The sticky
    // `tornDown` latch, checked alongside it on the success path below, catches
    // the plain teardown — with or without a re-sign-in — that this refresh
    // either straddled or started after.
    const generationAtStart = authGeneration;
    // ENG-2756 (P1) — re-assert the teardown latch + race token after EVERY
    // suspension point, BEFORE any classification / emitAuthError below.
    // handleCallback clears `tornDown` without bumping `authGeneration`, so a
    // stale in-flight refresh from session A that resolves with a 4xx /
    // missing-token / bad-audience (or that the transient/network path would
    // emitAuthError on for an expired current session) must be DISCARDED here —
    // not allowed to tear down the live, possibly re-signed-in session B it no
    // longer owns. Throws a plain Error (never TransientRefreshError) so nothing
    // retries. The success-path adoption below reuses the same guard.
    const discardIfSuperseded = (): void => {
      if (tornDown || authGeneration !== generationAtStart) {
        throw new Error("Refresh completed after teardown; discarding token");
      }
    };
    const path = targetTenantId
      ? `/refresh?target_tenant_id=${encodeURIComponent(targetTenantId)}`
      : "/refresh";
    let res: Response;
    try {
      res = await authFetch(path, { method: "POST" });
    } catch (networkErr) {
      // fetch rejected (offline, DNS, TLS, CORS-blocked): an outage, not a dead
      // session. Transient while the token is still valid — but discard first if
      // a teardown / re-sign-in landed while this request was in flight, so the
      // transient path cannot emitAuthError on a session this refresh has lost.
      discardIfSuperseded();
      throw transientOrTeardown(networkErr);
    }
    // Suspension point cleared (authFetch resolved) — re-check before the 5xx /
    // 4xx classification so neither transientOrTeardown nor the 4xx emitAuthError
    // can fire for a superseded refresh.
    discardIfSuperseded();
    if (!res.ok) {
      // ENG-2756 fix round 2 (R1) — TRANSIENT (retry): 5xx temporarily_unavailable,
      // 408 request timeout, 429 throttle. The auth service, or an edge in front of
      // it (API Gateway / ALB / CloudFront), could not COMPLETE verification or is
      // rate-limiting — NOT proof the grant is bad. The server itself returns 503 for
      // a verification outage (services/auth-service/src/oidc/token.ts); 408/429 only
      // ever originate at the edge. All three are retryable, so the scheduler keeps
      // trying instead of signing the user out.
      if (res.status >= 500 || res.status === 408 || res.status === 429) {
        throw transientOrTeardown(new Error(`Refresh failed (${res.status})`));
      }
      // ENG-2756 fix round 2 (R1) — the ONE authz denial that must NOT sign the client
      // out: a switchTenant() (targetTenantId set) rejected with 403
      // `invalid_target_tenant`. The user asked for a tenant they cannot access, but
      // the tenant they are already in is untouched. This is the only case round-1 P4
      // was meant to carve out. Read the error body to tell it apart from a 403
      // `forbidden_origin` (handled by the teardown default below), which is an
      // Origin-allowlist/CSRF rejection and NOT tenant-specific. Reject WITHOUT
      // teardown, leaving currentSession / currentAccessToken intact; a plain Error
      // (never TransientRefreshError) so the scheduler does not retry an authz denial.
      //
      // ENG-3188 — gate on TRUTHINESS, not `!== undefined`, to match the path-build
      // above (`targetTenantId ? "/refresh?..." : "/refresh"`): switchTenant("") builds
      // a PLAIN /refresh, so a 403 on it is a plain-refresh rejection, not a tenant
      // switch, and must fall through to the teardown default like any other 403.
      if (targetTenantId && res.status === 403) {
        let tenantSwitchDenied = false;
        try {
          const errorBody = (await res.json()) as { error?: string };
          tenantSwitchDenied = errorBody?.error === "invalid_target_tenant";
        } catch (readErr) {
          // ENG-3188 — distinguish HOW the body read failed. A JSON `SyntaxError` on a
          // fully-received body is a genuine malformed/empty 403 (not a tenant denial):
          // leave tenantSwitchDenied false and fall through to the teardown default below
          // — today's behavior. Any OTHER rejection is a network/stream read failure
          // mid-body (a dropped stream throws a TypeError; an aborted read throws an
          // AbortError/DOMException — neither is a SyntaxError): an outage, not a
          // proven-dead session, so treat it as TRANSIENT — discard first if a
          // teardown/re-sign-in superseded this refresh, otherwise reject via
          // transientOrTeardown so the CURRENT session is PRESERVED (the value here is
          // keeping the session, not retrying THIS switch — switchTenant is caller-driven,
          // not scheduler-retried; the background scheduler keeps refreshing on its own).
          // Tearing down here would be the sign-out-on-a-blip this ticket family fights.
          //
          // KNOWN LIMIT: a body that arrives TRUNCATED but closes cleanly (a flaky
          // proxy/LB emitting a short or non-JSON 403) also throws SyntaxError, so it
          // still tears down — the discriminator narrows the blip window to mid-stream
          // failures, it does not close it. Distinguishing further would need a body the
          // read already lost.
          if (!(readErr instanceof SyntaxError)) {
            discardIfSuperseded();
            throw transientOrTeardown(readErr);
          }
        }
        // res.json() is a fresh suspension point: re-assert the latch + race token
        // before the teardown branch below can emitAuthError on a superseded session.
        discardIfSuperseded();
        if (tenantSwitchDenied) {
          throw new Error(`Refresh failed (${res.status})`);
        }
      }
      // ENG-2756 fix round 2 (R1) — DEFINITIVE (tear down, then throw). Restores the
      // pre-P4 deterministic default so a plain Error can never silently kill the
      // scheduler. Covers: 401 `invalid_grant` (revoked/expired/missing session →
      // session_expired); 400 `invalid_request` (missing/ambiguous refresh cookie =
      // the session is over → refresh_failed); 403 `forbidden_origin` (an
      // Origin-allowlist/CSRF rejection that can fire on ANY /refresh, including a
      // background one — NOT a switchTenant tenant-access denial, so it is NOT
      // exempted); and any other unexpected 4xx.
      const err: AuthError = {
        code: res.status === 401 ? "session_expired" : "refresh_failed",
        message: `Refresh failed (${res.status})`,
        hint: "Sign in again",
      };
      emitAuthError(err);
      throw new Error(err.message);
    }
    // /refresh returns OAuth-standard `access_token` (snake_case). Earlier
    // code read `accessToken` which was always undefined → silent failure of
    // every cold-load/silent-refresh path. Fixed 2026-04-27.
    const body = (await res.json()) as { access_token?: string; accessToken?: string };
    // Suspension point cleared (res.json awaited) — re-check before the
    // missing-token / bad-audience emitAuthError branches below.
    discardIfSuperseded();
    const accessToken = body.access_token ?? body.accessToken;
    if (!accessToken) {
      const err: AuthError = {
        code: "refresh_failed",
        message: "Refresh response missing access_token",
        hint: "Sign in again",
      };
      emitAuthError(err);
      throw new Error(err.message);
    }
    // ENG-119 defense in depth — same check as adoptOidcUser. Normally
    // unreachable once the audience-pinned authFetch header above is in
    // place (a mismatched cookie 401s before minting anything), but this
    // guards the adoption itself against a server-side regression rather
    // than trusting the request layer alone.
    if (!tokenAudienceMatches(accessToken)) {
      const err: AuthError = {
        code: "refresh_failed",
        message: "Refresh response audience did not match this app",
        hint: "Sign in again",
      };
      emitAuthError(err);
      throw new Error(err.message);
    }
    // ENG-2756 fix round 2 — refuse adoption on a torn-down or superseded client.
    // `tornDown` (sticky latch): a teardown (signOut / emitAuthError) landed while
    // this /refresh was in flight, OR this refresh was launched after one — either
    // way the session is intentionally dead, and adopting now would repopulate it
    // and emit a live session to every listener, silently signing the user back in
    // (the security case: a signOut() whose /logout failed at the network layer
    // while the document outlives the sign-out — see the `tornDown` declaration for
    // why the unconditional postLogoutUri navigation does not close that window).
    // `authGeneration` (race
    // token): a teardown then a NEW sign-in happened, which cleared the latch but
    // must still discard THIS stale in-flight token in favour of the freshly-minted
    // session. Throw a plain Error (never TransientRefreshError) so nothing retries
    // — the teardown was deliberate.
    //
    // NOTE (ENG-2756 fix round 2, R3): today this call is REDUNDANT with the
    // post-json discardIfSuperseded() above — nothing between the two suspends (the
    // missing-token and audience checks are synchronous), so the latch and race token
    // cannot change in between. Kept as pure defence-in-depth: it is the last guard
    // immediately before the adoption, and would still hold if a future `await` were
    // ever introduced between the post-json check and this point.
    discardIfSuperseded();
    currentAccessToken = accessToken;
    currentSession = sessionFromAccessToken(accessToken);
    scheduler.schedule(currentSession.expiresAt);
    emitSession();
    return currentSession;
  }

  const scheduler: SilentRefreshScheduler = createSilentRefreshScheduler(
    async () => {
      await performRefresh();
    },
    (err) => {
      // What reaches here is NOT the common path (a transient failure mid-window
      // is retried by the scheduler and never surfaces). Two things do: the
      // scheduler's expiry terminator (silent-refresh.ts) once an outage outlasts
      // the token, and a residual definitive failure that did not already cancel
      // the scheduler — in practice only a malformed-200 parse throw, since every
      // emitAuthError-driven teardown (4xx, missing/bad token, audience mismatch)
      // calls scheduler.cancel() FIRST, which bumps the scheduler's generation so
      // arm()'s handler returns before reaching this callback. Either way the
      // session is already down; log for telemetry. safeWarn (ENG-2907), not a
      // bare console.warn: a patched logger must not reject the scheduler's
      // internal `.then(undefined, handler)` and surface as an unhandled rejection.
      safeWarn("[auth-client] silent refresh failed", err);
    },
  );

  // -- Public API ------------------------------------------------------------

  async function signIn(opts: SignInOptions = {}): Promise<void> {
    // ENG-2756 fix round 2 (F-9) — signIn() deliberately does NOT clear the latch.
    // A redirect sign-in always lands in a NEW document where `tornDown` starts
    // false, so it never needs the clear to succeed. Clearing it here is instead a
    // hole: when signinRedirect()'s OIDC discovery fetch rejects on the same outage
    // that failed /logout, the cleared latch would let the auth gate's next
    // getAccessToken() cold-load off the still-live refresh
    // cookie — signing the user back in after a failed sign-out. (That compose is
    // live even though ENG-2907 navigates unconditionally: the wipe emits the null
    // session BEFORE the navigate, so the auth gate re-renders and fires signIn()
    // while the postLogoutUri navigation is still pending — and on this outage it
    // never commits. See the `tornDown` declaration.) handleCallback()
    // is the ONLY latch-clear, and it is the only site holding a server-minted
    // credential. The clear is also useless even when signinRedirect() resolves:
    // the document is alive until the navigation's unload commits.
    const extraQueryParams: Record<string, string> = {};
    if (opts.tenantId) extraQueryParams.target_tenant_id = opts.tenantId;
    await userManager.signinRedirect({
      extraQueryParams,
      // ENG-101 — `prompt` and `login_hint` are passed straight through to
      // GET /authorize. `prompt: 'login'` forces the hosted sign-in form
      // even when a refresh cookie (host-only on the shared auth host) for a
      // DIFFERENT identity is present, so an explicit sign-in attempt can never
      // be silently resolved to whatever account that stale cookie belongs to.
      ...(opts.prompt ? { prompt: opts.prompt } : {}),
      ...(opts.loginHint ? { login_hint: opts.loginHint } : {}),
      state: opts.returnTo
        ? { returnTo: opts.returnTo }
        : { returnTo: typeof window !== "undefined" ? window.location.href : "" },
    });
  }

  async function handleCallback(): Promise<Session> {
    try {
      const user = await userManager.signinRedirectCallback();
      // ENG-2756 fix round 2 — a fresh, server-minted user in hand is an explicit
      // new sign-in: clear the teardown latch. MUST precede adoptOidcUser, whose
      // reject branch refuses while the latch is still set.
      //
      // A5 — the window from this clear until adoptOidcUser either establishes the
      // session or is re-latched below is SYNCHRONOUS: adoptOidcUser contains no
      // await, so no event-loop turn can interleave, and no concurrent teardown or
      // in-flight refresh/cold-load continuation can land inside it. adoptOidcUser
      // already fires a fire-and-forget `void userManager.removeUser()`, so the
      // temptation to `await` it (here or inside it) exists — DO NOT, and do not add
      // an await here either. Keep this window synchronous.
      //
      // The window is NOT unobservable, though, and an earlier version of this
      // comment claimed it was. adoptOidcUser calls emitSession() INSIDE it, so a
      // re-entrant onSessionChange listener that synchronously calls
      // getAccessToken() starts a cold-load while `tornDown` is still false: a /me
      // probe does fire on a client that ends this callback torn down (measured on
      // the reject path: one extra /me, where three other tests in the latch suite
      // assert zero). No bearer escapes — but the backstop is performRefresh's ENTRY
      // guard, not the synchronicity. The cold-load parks on its first await; the
      // re-latch below has run by the time /me resolves; performRefresh then refuses
      // and the cold-load resolves null. That is precisely what an await in this
      // window would break: it would let the parked /me resolve BEFORE the re-latch
      // and turn the probe into a resurrection.
      tornDown = false;
      let session: Session | null;
      try {
        session = adoptOidcUser(user);
      } catch (adoptErr) {
        // F-15 (throw path) — adoptOidcUser does not only return null on refusal,
        // it can THROW: tokenAudienceMatches() → jose decodeJwt() throws on a
        // non-empty, non-JWT access_token, evaluated in adoptOidcUser's guard
        // BEFORE any session is established. Left to unwind, that throw skips the
        // returned-null re-latch below and reaches the outer catch with the latch
        // still cleared — the same post-signOut resurrection F-15 closes for the
        // returned-null case. Re-latch here too, then rethrow.
        //
        // UNCONDITIONAL (fix round 3). This was `if (!currentSession)`, guarding
        // against a throwing onSessionChange listener unwinding here from
        // adoptOidcUser's SUCCESS path with a live session in hand. That predicate
        // asked "does a session exist", not "did THIS adoption establish one", and
        // signOut() makes those different questions: it latches BEFORE the /logout
        // await and nulls currentSession only after. ENG-2907 split that ordering
        // across two files without changing it — signOut() (below) calls
        // beginTeardown() synchronously and then delegates to createSignOutFlow's
        // returned function, which awaits authFetch("/logout") and only nulls
        // currentSession afterwards, in wipeLocalSessionState's clearInMemorySession
        // step. The window is now BOUNDED by that flow's SIGN_OUT_LOGOUT_TIMEOUT_MS
        // (10s) rather than by an unbounded fetch, but it is still a full network
        // round-trip wide: a malformed callback landing in it read a session
        // signOut() had not torn down yet, skipped the re-latch, and left `tornDown`
        // false permanently — resurrecting a SUCCESSFUL sign-out. `currentSession ===
        // priorSession` fails the mirror case for the same reason (the reject branch
        // nulls currentSession and THEN emits). Neither predicate is needed, because
        // notifyListeners now contains BOTH halves of a listener fan-out failure: a
        // synchronous listener throw (per listener) and its own catch body (fix round
        // 4 protected that body, today via ENG-2907's safeWarn; unprotected, a patched
        // console turned every contained throw back into an escaping one and fired
        // this beginTeardown() on a live, valid session while getAccessToken() still
        // handed out the bearer — measured at d1d4c6f0b). With both contained, the
        // path into this catch that the SUITE exercises is adoptOidcUser's own guard
        // refusing — decodeJwt throwing on a non-JWT access_token — which throws
        // before EITHER assignment, so no state from THIS adoption is set. Note how
        // narrow that claim is: PRIOR state is
        // untouched, and beginTeardown() only flips the latch and bumps the
        // generation — it clears neither currentSession nor currentAccessToken. A
        // successful callback followed by one carrying `access_token: 'not-a-jwt'`
        // therefore ends LATCHED but still holding the earlier session:
        // getAccessToken() returns the prior bearer and getCurrentUser() the prior
        // session, while performRefresh refuses at entry ("Refresh on a torn-down
        // client; not sending /refresh") so nothing NEW can be minted (measured at
        // 94a04da44). Pre-existing, not a fix-round-4 regression — that round's
        // only code change here was wrapping the console.warn, which is off this
        // path — and having beginTeardown() clear session state is ticketed
        // separately. Nothing left to condition on: latch.
        //
        // RESIDUAL, accepted and NOT defended: `for (const listener of listeners)`
        // drives the iterator OUTSIDE the per-listener try — every part of it. So a
        // patched `Set.prototype[Symbol.iterator]` (measured, fix round 4) and a
        // patched `%SetIteratorPrototype%.next` that throws mid-fan-out, after the
        // first listener has already run (measured at 94a04da44), unwind to here
        // identically; both reach it from adoptOidcUser's SUCCESS path, where
        // currentSession is already set, so both land in the same mixed state this
        // round closed — latched, with the just-adopted bearer still live.
        // `scheduler.schedule()` — the other post-assignment statement in
        // adoptOidcUser — belongs to the same residual for the same reason: it bottoms
        // out in the global timer functions, and a patched/throwing setTimeout is the
        // same instrumentation-SDK accident class (this suite's own MUST-FIX-2
        // sign-out test stubs setTimeout, so it is not hypothetical tooling).
        // Defending
        // it means hoisting emitSession() out of adoptOidcUser into its callers,
        // which is the better shape but touches the ENG-1753 cold-load call site and
        // its pinned emit-ordering tests; ticketed separately rather than done here.
        // The realistic trigger is not an attacker: anyone who can patch Set's
        // iterator already owns the page and has shorter paths. It is a benign
        // third-party polyfill or an instrumentation-SDK bug — the same class of
        // accident as the patched console above.
        beginTeardown();
        throw adoptErr;
      }
      if (!session) {
        // F-15 (returned-null path) — adoptOidcUser refused this callback (token-less
        // / wrong-audience user) and cleared local state, but its reject branch does
        // NOT latch (F-11). Having just cleared the latch above, a failed sign-in
        // would otherwise leave the client un-latched: the next getAccessToken()
        // would cold-load off the still-live refresh cookie and silently sign the
        // user back in. Re-latch so a failed callback leaves the client torn down.
        beginTeardown();
        throw new Error("Callback returned no session");
      }
      markSignedInNow();
      // Hand the just-minted session (token in hand) to any cold-load that
      // deferred to us during this callback (ENG-1753).
      initialCallbackDeferred.resolve(session);
      return session;
    } catch (err) {
      // Unblock a waiting cold-load so it falls through to the cookie-probe
      // path instead of stalling on us.
      initialCallbackDeferred.resolve(null);
      throw err;
    }
  }

  async function getAccessToken(): Promise<string | null> {
    // ENG-2756 (P3) — a torn-down client hands out NO bearer, even while stale
    // `currentAccessToken` / `currentSession` linger (beginTeardown does not yet
    // null them — that structural fix is ENG-3156). Without this, the /logout
    // window and a malformed-callback-after-a-live-session both serve the prior
    // token. Fast-path null before the cached-token branch below.
    if (tornDown) return null;
    if (currentAccessToken && currentSession) {
      const msUntilExpiry = currentSession.expiresAt * 1000 - Date.now();
      // 30s, DELIBERATELY not silent-refresh.ts's LEAD_TIME_MS (5 min). These
      // serve different jobs: LEAD_TIME_MS is how early the BACKGROUND scheduler
      // proactively re-mints so an outage has runway to clear. This threshold is
      // the last-resort SYNCHRONOUS refresh on the hand-out path — it only fires
      // when the scheduler failed to keep the token fresh (a throttled/
      // backgrounded tab whose timers were suspended), so it is intentionally
      // tight: refresh only when the token is essentially spent, not 5 minutes
      // out on every getAccessToken call. Keep the two decoupled on purpose.
      if (msUntilExpiry > 30_000) return currentAccessToken;
      // Within the lead window — refresh now.
      try {
        await performRefresh();
        return currentAccessToken;
      } catch {
        return null;
      }
    }
    // No in-memory token. Try cold-load from /me (refresh cookie may exist).
    const session = await getCurrentUser();
    return session ? currentAccessToken : null;
  }

  async function getCurrentUser(): Promise<Session | null> {
    // ENG-2756 fix round 2 — the teardown latch short-circuits every cold-load.
    // After signOut()/emitAuthError() a cold-load must not probe /me or adopt a
    // cached oidc-client-ts user and silently re-mint a session on a torn-down
    // client. Sticky until an explicit new sign-in clears it.
    // (P3) — checked BEFORE the `currentSession` short-circuit so a torn-down
    // client does not keep serving the prior in-memory session while
    // beginTeardown still leaves it set (structural clear is ENG-3156).
    if (tornDown) return null;
    if (currentSession) return currentSession;
    if (coldLoadInflight) return coldLoadInflight;

    coldLoadInflight = (async () => {
      try {
        // ENG-1753 — first-load OIDC callback sequencing. While this page load
        // is a `?code=…&state=…` callback, the session is minted by
        // handleCallback's /token exchange (the only path that yields both an
        // access token and the refresh cookie). Wait for it and adopt its
        // result rather than racing it with a cookie probe that has no bearer
        // and no cookie yet — the premature 401 that hung the analytics MFE.
        // See the initialCallbackDeferred doc comment above.
        if (isOidcCallbackInProgress()) {
          const settled = await Promise.race([
            initialCallbackDeferred.promise,
            new Promise<undefined>((r) =>
              setTimeout(() => r(undefined), INITIAL_CALLBACK_WAIT_MS),
            ),
          ]);
          if (settled) return settled;
          if (currentSession) return currentSession;
          // handleCallback produced no session (or timed out) — fall through to
          // the normal cold-load below.
        }
        // Cheap path first: oidc-client-ts persists the OIDC user to
        // sessionStorage on signinRedirectCallback. Page reloads (e.g. our
        // AuthCallback redirecting to "/") wipe in-memory state but leave
        // the user in storage — adopt it without an extra round-trip.
        try {
          const cached = await userManager.getUser();
          if (cached && !cached.expired && cached.access_token) {
            const adopted = adoptOidcUser(cached);
            if (adopted) {
              // The cached user is trusted on `exp` alone here — it proves
              // nothing about whether the session behind it is still valid
              // server-side. A `/logout` elsewhere in this cross-app SSO
              // group revokes the session (denylist) but this per-tab cache
              // has no way to learn that, so a revoked session would
              // otherwise resurrect forever. Fire a background /me probe
              // (cookie-based, same as the cold-load-from-scratch path
              // below) and tear the session down the moment we learn it's
              // dead — without blocking the fast path's synchronous return.
              //
              // Staging regression (2026-07-09): a plain 401 from /me is
              // NOT proof the session is dead. /me returns 401 for several
              // reasons — no cookie present, a transient verify failure —
              // that are ambiguous, not a confirmed revocation (see
              // RevokedTokenError's doc comment in
              // ../../../services/auth-service/src/tokens/mint.ts). On
              // staging, /me was already known to 401 spuriously during the
              // PKCE handshake (harmless pre-existing noise, since nothing
              // consumed it destructively before this probe existed); wiring
              // *any* 401 here to teardown turned that pre-existing noise
              // into a forced logout on every reload. Only tear down when
              // the body proves it: `error: "invalid_token"` is the
              // definitive shape handleMe emits for a store-confirmed-revoked
              // session or a denylist/watermark hit — everything else
              // (`no_session`, or an unparseable/empty body) is left alone.
              //
              // ENG-138: skip the probe entirely inside the post-sign-in
              // grace window (see withinPostSignInGraceWindow's doc comment
              // above) — a session minted moments ago cannot yet be
              // revoked, so this is the exact spurious-401 case the comment
              // above already describes, not a real check being skipped.
              // (P2) — a latched client must not fire /me at all (handleMe bumps
              // last_active + re-sets the refresh-cookie Max-Age, prolonging the very
              // session sign-out is ending).
              //
              // NOTE (ENG-2756 fix round 2, R3): the `!tornDown` disjunct here is
              // pure defence-in-depth — it cannot short-circuit TODAY. adoptOidcUser
              // returns null while tornDown (its own entry guard), and nothing
              // suspends between that adopt and here, so a non-null `adopted` proves
              // tornDown was false at adoption and is still false now. The load-bearing
              // teardown guard on the cold-load path is the `if (tornDown) return null`
              // at the cookie-probe below, which IS reachable after a suspension. Kept
              // so this probe stays safe if a future `await` is introduced above it.
              if (!tornDown && !withinPostSignInGraceWindow()) {
                void authFetch("/me", { method: "GET" })
                  .then(async (res) => {
                    if (res.status !== 401) return;
                    let definitivelyRevoked = false;
                    try {
                      const body = (await res.json()) as { error?: string };
                      definitivelyRevoked = body.error === "invalid_token";
                    } catch {
                      // Empty/non-JSON 401 body — not a definitive signal.
                    }
                    if (definitivelyRevoked) {
                      emitAuthError({
                        code: "session_expired",
                        message: "Session revoked",
                        hint: "Sign in again",
                      });
                    }
                  })
                  .catch(() => {
                    // Transient network failure on the background probe —
                    // don't tear down a possibly-still-valid session over it.
                  });
              }
              return adopted;
            }
          }
        } catch {
          // Fall through to the cookie probe.
        }
        // ENG-2756 (P2) — a teardown that landed during the userManager.getUser()
        // await above falls through to here; a latched client must NOT send this
        // cold-load /me probe (handleMe bumps last_active + re-sets the refresh
        // cookie Max-Age, extending the session sign-out is ending). Mirror
        // performRefresh's F-12 entry guard.
        if (tornDown) return null;
        // Probe the cookie. 200 means a valid refresh cookie is on this
        // request; 401 means sign in from scratch. The probe body is
        // metadata-only — we still need to mint an access token via
        // /refresh (the cold-load resurrection path).
        const probe = await authFetch("/me", { method: "GET" });
        if (!probe.ok) return null;
        // Mint a fresh access token + rotate the refresh cookie.
        const session = await performRefresh();
        return session;
      } catch {
        return null;
      } finally {
        coldLoadInflight = null;
      }
    })();
    return coldLoadInflight;
  }

  // ENG-2907 moved the sign-out ORCHESTRATION into sign-out-flow.ts. The ENG-2756
  // TEARDOWN LATCH stays here, because `tornDown` / `authGeneration` live in this
  // closure and every site that reads them (performRefresh, adoptOidcUser,
  // getCurrentUser, handleCallback) is in this file. `createSignOutFlow` therefore
  // stays latch-agnostic: it is handed only the teardown steps it owns.
  const runSignOutFlow = createSignOutFlow({
    scheduler,
    authFetch,
    userManager,
    config,
    // currentSession / currentAccessToken live in this closure; the flow nulls
    // them and emits the resulting null session through this callback.
    clearInMemorySession: () => {
      currentSession = null;
      currentAccessToken = null;
      emitSession();
    },
    signedInAtStorageKey: SIGNED_IN_AT_STORAGE_KEY,
    // oidc-client-ts composes its cached-user key as the WebStorageStateStore
    // `oidc.` prefix + `user:<authority>:<client_id>` (authority = authBaseUrl,
    // client_id = cognitoAppClientId — the same settings the UserManager above
    // is built from). The wipe deletes this synchronously; see SignOutFlowDeps.
    oidcUserStorageKey: `oidc.user:${config.authBaseUrl}:${config.cognitoAppClientId}`,
  });

  /**
   * ENG-2756 fix round 2, re-sited onto ENG-2907's extracted flow — latch the
   * teardown BEFORE anything the flow awaits. This wrapper's body is synchronous
   * and `beginTeardown()` is its FIRST statement, so the latch (sticky flag +
   * race token) is set before `createSignOutFlow`'s `await authFetch("/logout")`
   * is even reached. Any silent refresh or cold-load resolving during or after
   * that round-trip refuses to adopt and cannot resurrect the session we are
   * ending. The window in which `tornDown === true` while `currentSession` is
   * still set now runs from here until the flow's `clearInMemorySession()`, which
   * is the FIRST statement of its wipe. Everything in between is synchronous apart
   * from the one `await authFetch("/logout")`, so since ENG-2907 the window is
   * BOUNDED by SIGN_OUT_LOGOUT_TIMEOUT_MS (10s) — for any fetch impl that honours
   * the AbortSignal the flow passes; an injected one that ignores it is unbounded
   * again. Bounded or not it is a full network round-trip wide, and it is what
   * handleCallback's UNCONDITIONAL throw-path re-latch exists for (see there).
   *
   * NOT `async`, deliberately: this returns the flow's promise by identity, so
   * overlapping callers still observe the flow's single-flight memo (one POST
   * /logout, one shared promise). An `async` wrapper would allocate a fresh
   * promise per call.
   *
   * beginTeardown() runs on EVERY call, including one that the memo DEDUPES into
   * an already-running flow, so `authGeneration` can be bumped more than once per
   * POST /logout. That is harmless and is the safer of the two placements:
   *   - `tornDown` is a sticky boolean; setting it twice is a no-op.
   *   - `authGeneration` is only ever read as `generationAtStart` (captured in
   *     performRefresh before its network call) and compared for INEQUALITY on the
   *     success path. Nothing requires it to hold a particular value, so an extra
   *     bump can only invalidate MORE in-flight work, never less.
   *   - Bumping inside the flow (after the memo check) would SKIP the dedupe path:
   *     a second, explicit signOut() that lands after a handleCallback() cleared
   *     the latch mid-flow would then leave the client un-latched, with an
   *     in-flight refresh free to adopt on a client the user just asked to sign
   *     out of again.
   */
  function signOut(options?: SignOutOptions): Promise<SignOutResult> {
    beginTeardown();
    return runSignOutFlow(options);
  }

  async function switchTenant(targetTenantId: string): Promise<Session> {
    return performRefresh(targetTenantId);
  }

  async function listTenantMemberships(): Promise<Tenant[]> {
    const res = await authFetch("/me/tenants", { method: "GET" });
    if (!res.ok) {
      throw new Error(`Failed to list tenant memberships (${res.status})`);
    }
    const body = (await res.json()) as { tenants: Tenant[] };
    return body.tenants;
  }

  function onSessionChange(listener: SessionListener): () => void {
    sessionListeners.add(listener);
    return () => {
      sessionListeners.delete(listener);
    };
  }

  function onAuthError(listener: AuthErrorListener): () => void {
    errorListeners.add(listener);
    return () => {
      errorListeners.delete(listener);
    };
  }

  return {
    signIn,
    handleCallback,
    getCachedAccessToken: () => !tornDown && currentSession && currentSession.expiresAt * 1000 > Date.now() ? currentAccessToken : null,
    getAccessToken,
    getCurrentUser,
    signOut,
    switchTenant,
    refreshSession: () => performRefresh(),
    listTenantMemberships,
    onSessionChange,
    onAuthError,
    emitAuthError,
    config,
  };
}
